import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { ChevronDown, Car, Shield, Star, MapPin } from "lucide-react";

const formatPrice = (price, currency) => {
  if (!price) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "EUR",
      minimumFractionDigits: 2,
    }).format(price);
  } catch {
    return `${currency || ""} ${price}`;
  }
};

// ── Sub-components ────────────────────────────────────────────────────────────

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);

const StatCard = ({ icon, value, label }) => (
  <div className="bg-white/10 border border-white/10 rounded-xl p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center text-primary flex-shrink-0">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="font-heading text-lg font-semibold text-white leading-tight truncate">{value}</p>
      <p className="font-body text-[10px] uppercase tracking-widest font-medium text-white/50 truncate">{label}</p>
    </div>
  </div>
);

const CarCard = ({ car }) => {
  const displayPrice = car.price
    ? formatPrice(car.price, car.currency)
    : car.priceFormatted || "—";

  return (
    <div className="group bg-surface rounded-xl border border-surface-border overflow-hidden hover:shadow-card transition-all duration-300 flex flex-col">
      <div className="relative flex items-center justify-center p-6 aspect-[16/9]">
        <img
          src={car.image}
          alt={car.name}
          className="max-w-full max-h-36 object-contain scale-110 group-hover:scale-125 transition-transform duration-500"
          onError={(e) => { e.target.style.opacity = "0.3"; }}
        />
        <span className="absolute top-3 left-3 bg-text text-white px-3 py-1 rounded-full font-heading text-[10px] font-semibold uppercase tracking-wider">
          {car.type}
        </span>
      </div>
      <div className="p-5 flex flex-col flex-grow">
        <div className="flex justify-between items-start gap-2 mb-3">
          <h3 className="font-heading text-sm font-semibold text-text leading-snug">{car.name}</h3>
          <div className="text-right flex-shrink-0">
            <span className="font-heading text-lg font-bold text-text">{displayPrice}</span>
            <span className="font-body text-[11px] block text-text-muted uppercase tracking-wide">/ day</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(car.specs || {}).map(([key, val]) => (
            <span key={key} className="font-body text-[11px] bg-surface-raised text-text-muted px-2 py-1 rounded-md border border-surface-border">
              {val} {key}
            </span>
          ))}
        </div>
        <a
          href="/"
          className="mt-auto w-full py-2.5 rounded-lg bg-primary hover:bg-primary-hover text-white font-heading text-xs font-semibold transition-colors duration-200 text-center block uppercase tracking-wider"
        >
          Reserve Now
        </a>
      </div>
    </div>
  );
};

const FaqItem = ({ faq, isOpen, onToggle }) => (
  <div className="border border-surface-border rounded-xl overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left bg-surface hover:bg-surface-raised transition-colors"
      aria-expanded={isOpen}
    >
      <span className="font-heading font-semibold text-text text-sm sm:text-base">{faq.question}</span>
      <ChevronDown size={18} className={`flex-shrink-0 text-primary transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
    </button>
    {isOpen && (
      <div className="px-6 pb-5 pt-1 bg-surface border-t border-surface-border">
        <p className="font-body text-text-muted text-sm leading-relaxed">{faq.answer}</p>
      </div>
    )}
  </div>
);

const LocationCard = ({ location, vendorSlug }) => (
  <a
    href={vendorSlug ? `/vendors/${vendorSlug}/locations/${location.slug}` : `/locations/${location.slug}`}
    className="group bg-surface border border-surface-border rounded-xl p-5 hover:border-primary/40 hover:shadow-card transition-all flex items-center justify-between"
  >
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-9 h-9 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
        <MapPin size={16} className="text-accent-foreground" />
      </div>
      <div className="min-w-0">
        <h4 className="font-heading text-sm font-semibold text-text truncate">{location.name}</h4>
        <p className="font-body text-xs text-text-muted mt-0.5">Starting from</p>
      </div>
    </div>
    <div className="text-right flex-shrink-0 ml-4">
      <p className="font-heading text-base font-bold text-text">
        {location.minPrice?.formatted || formatPrice(location.minPrice?.amount, location.minPrice?.currency)}
      </p>
      <p className="font-body text-[10px] uppercase tracking-wide text-text-muted">/ day</p>
    </div>
  </a>
);

// ── Main Component ────────────────────────────────────────────────────────────

const VendorView = ({ data }) => {
  const [openFaq, setOpenFaq] = useState(null);

  if (!data || !data.vendor) return <LoadingSpinner />;

  const { vendor, featuredCollections, howItWorks, faqs, topLocations } = data;
  const logoSrc = vendor?.logo?.url;
  const hasHowItWorks = howItWorks && howItWorks.length > 0;

  return (
    <div className="min-h-screen font-body mt-10">
      <Helmet>
        <title>{data.seo?.title || `${vendor.name} - Car Rental`}</title>
        {data.seo?.metaDescription && <meta name="description" content={data.seo.metaDescription} />}
        {data.seo?.canonicalUrl && <link rel="canonical" href={data.seo.canonicalUrl} />}
      </Helmet>

      {/* ── HERO ── */}
      <section className="bg-surface border-b border-surface-border px-4 sm:px-6 py-10 sm:py-14">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center gap-8 sm:gap-12">

          <div className="flex-1 space-y-5 text-center sm:text-left">
            {/* Verified badge */}
            <div className="inline-flex items-center gap-2 border border-primary/30 bg-accent px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 rounded-full bg-primary" />
              <span className="font-heading text-[11px] font-semibold text-accent-foreground uppercase tracking-widest">
                Verified · {vendor.rating}/5 Rating · Premium Partner
              </span>
            </div>

            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-text leading-tight">
              Experience <span className="text-primary">{vendor.name}</span>
            </h1>

            <p className="font-body text-text-muted leading-relaxed max-w-lg mx-auto sm:mx-0">
              {vendor.description}
            </p>

            {vendor.fleetHighlights?.length > 0 && (
              <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                {vendor.fleetHighlights.map((h) => (
                  <span key={h} className="font-body text-xs bg-accent text-accent-foreground px-3 py-1 rounded-full border border-primary/20">
                    {h}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Logo card */}
          <div className="w-full sm:w-64 lg:w-72 bg-surface border border-surface-border rounded-2xl shadow-card flex items-center justify-center p-8 min-h-[160px] flex-shrink-0">
            {logoSrc ? (
              <img src={logoSrc} alt={vendor.logo?.alt || vendor.name} className="max-h-24 max-w-full object-contain" />
            ) : (
              <span className="font-heading text-2xl font-bold text-text">{vendor.name}</span>
            )}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="px-4 sm:px-6 py-5">
        <div className="max-w-6xl mx-auto">
          <div className="bg-dark rounded-xl p-5 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={<Car size={18} />} value={vendor.stats?.vehicles || "—"} label="Vehicles" />
              <StatCard icon={<Shield size={18} />} value={vendor.stats?.insured || "—"} label="Insured" />
              <StatCard icon={<Star size={18} />} value={vendor.reviewCount?.toLocaleString() || "—"} label={vendor.stats?.reviewsLabel || "Reviews"} />
              <StatCard icon={<MapPin size={18} />} value={vendor.stats?.locations || "—"} label="Locations" />
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURED CARS ── */}
      {featuredCollections?.length > 0 && (
        <section className="px-4 sm:px-6 py-10">
          <div className="max-w-6xl mx-auto">
            {featuredCollections.map((collection, idx) => (
              <div key={idx} className={idx < featuredCollections.length - 1 ? "mb-14" : ""}>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-text mb-1">
                  {collection.title || "Featured Collection"}
                </h2>
                <p className="font-body text-text-muted mb-8">
                  {collection.description || "Hand-picked vehicles for your next journey"}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {collection.cars?.map((car, cIdx) => (
                    <CarCard key={cIdx} car={car} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── TOP LOCATIONS ── */}
      {topLocations?.length > 0 && (
        <section className="px-4 sm:px-6 py-10 bg-surface border-t border-surface-border">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-text mb-1">Top Locations</h2>
            <p className="font-body text-text-muted mb-8">Popular pickup points</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {topLocations.map((location) => (
                <LocationCard key={location.id} location={location} vendorSlug={vendor.slug} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── HOW IT WORKS (optional) ── */}
      {hasHowItWorks && (
        <section className="bg-dark px-4 sm:px-6 py-14 sm:py-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              Your Rental Process
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12">
              {howItWorks.map((step) => (
                <div key={step.step} className="text-center sm:text-left space-y-3">
                  <span className="font-heading text-4xl sm:text-5xl font-bold text-primary/50">{step.step}</span>
                  <h4 className="font-heading text-lg font-semibold text-white">{step.title}</h4>
                  <p className="font-body text-white/50 text-sm leading-relaxed">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── ABOUT + FAQ ── */}
      <section className="px-4 sm:px-6 py-14 sm:py-20 bg-surface">
        <div className="max-w-3xl mx-auto">
          {vendor.longDescription && (
            <div className="mb-14">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-text mb-4">
                About {vendor.name}
              </h2>
              <p className="font-body text-text-muted leading-relaxed">{vendor.longDescription}</p>
            </div>
          )}

          {faqs?.length > 0 && (
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-text mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <FaqItem
                    key={i}
                    faq={faq}
                    isOpen={openFaq === i}
                    onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default VendorView;
